/* global Word */
import { WordApiError } from '../../core/errors';
import { logger } from '../../core/logging';

const METADATA_NS = 'https://sbi-addin/template-metadata/v1';
const METADATA_KEY = 'activeTemplateId';

export function buildTemplateMetadataXml(templateId: string): string {
  return `<?xml version="1.0"?>
<metadata xmlns="${METADATA_NS}">
  <${METADATA_KEY}>${templateId}</${METADATA_KEY}>
</metadata>`;
}

/**
 * Writes the active template ID into the document as a Custom XML Part.
 * Called when the user applies a template.
 */
export async function persistTemplateId(
  context: Word.RequestContext,
  templateId: string
): Promise<void> {
  try {
    const xml = buildTemplateMetadataXml(templateId);

    const parts = context.document.customXmlParts;
    parts.load('items');
    await context.sync();

    for (const part of parts.items) {
      part.load('namespaceUri');
    }
    await context.sync();

    for (const part of parts.items) {
      if (part.namespaceUri === METADATA_NS) {
        part.delete();
      }
    }
    await context.sync();

    context.document.customXmlParts.add(xml);
    await context.sync();
    logger.info('Template ID persisted', { templateId });
  } catch (err) {
    throw WordApiError.fromOfficeError(err, { operation: 'persistTemplateId' });
  }
}

/**
 * Reads the template ID stored in the document.
 * Returns null if no SBI metadata part exists (e.g. a fresh document).
 */
export async function readPersistedTemplateId(
  context: Word.RequestContext
): Promise<string | null> {
  try {
    const parts = context.document.customXmlParts;
    parts.load('items');
    await context.sync();

    for (const part of parts.items) {
      part.load('namespaceUri');
    }
    await context.sync();

    const sbiPart = parts.items.find((part) => part.namespaceUri === METADATA_NS);
    if (!sbiPart) return null;

    const xml = sbiPart.getXml();
    await context.sync();
    const match = xml.value.match(new RegExp(`<${METADATA_KEY}>([^<]+)</${METADATA_KEY}>`));
    return match ? match[1] : null;
  } catch {
    logger.warn('Could not read persisted template ID, falling back to base', {});
    return null;
  }
}
